import { supabase } from './supabase';

export interface InvoiceItem {
  invoice_id: string;
  payment_intent_id: string;
  amount_cents: number;
  status: string;
  invoice_pdf_url: string | null;
  hosted_invoice_url: string | null;
  invoice_number: string | null;
  description: string | null;
  created_at: string;
}

export interface InvoiceDetails {
  invoice_data: {
    invoice_id: string;
    payment_intent_id: string;
    amount_cents: number;
    status: string;
    invoice_pdf_url: string | null;
    hosted_invoice_url: string | null;
    invoice_number: string | null;
    description: string | null;
    created_at: string;
  };
  line_items: Array<{
    description: string;
    quantity: number;
    unit_amount_cents: number;
    total_amount_cents: number;
  }>;
}

export const invoiceService = {
  // Create invoice for a successful payment
  async createInvoice(
    paymentIntentId: string,
    userId: string,
    tokenPackageId: string,
    amountCents: number
  ): Promise<{ success: boolean; invoice?: any; error?: string }> {
    try {
      console.log('📄 Creating invoice for payment:', paymentIntentId);
      
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL not configured');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/create-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          paymentIntentId,
          userId,
          tokenPackageId,
          amountCents,
          currency: 'usd'
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Invoice created successfully:', data.invoice_id);
        return { success: true, invoice: data };
      } else {
        const errorData = await response.json();
        console.error('❌ Invoice creation failed:', errorData);
        return { success: false, error: errorData.error || 'Failed to create invoice' };
      }
    } catch (error) {
      console.error('💥 Error creating invoice:', error);
      return { success: false, error: 'Failed to create invoice' };
    }
  },

  // Get user's invoice history
  async getInvoiceHistory(userId: string): Promise<InvoiceItem[]> {
    try {
      console.log('📊 Fetching invoice history for user:', userId);
      
      const { data, error } = await supabase.rpc('get_user_invoice_history', {
        p_user_id: userId
      });

      if (error) {
        console.error('❌ Error fetching invoice history:', error);
        throw error;
      }

      console.log('✅ Invoice history loaded:', data);
      return data || [];
    } catch (error) {
      console.error('💥 Error fetching invoice history:', error);
      return [];
    }
  },

  // Get detailed invoice information
  async getInvoiceDetails(invoiceId: string, userId: string): Promise<InvoiceDetails | null> {
    try {
      console.log('📄 Fetching invoice details:', invoiceId);
      
      const { data, error } = await supabase.rpc('get_invoice_details', {
        p_invoice_id: invoiceId,
        p_user_id: userId
      });

      if (error) {
        console.error('❌ Error fetching invoice details:', error);
        throw error;
      }

      if (data && data.length > 0) {
        console.log('✅ Invoice details loaded');
        return data[0];
      }

      return null;
    } catch (error) {
      console.error('💥 Error fetching invoice details:', error);
      return null;
    }
  },

  // Format amount for display
  formatAmount(amountCents: number): string {
    return `$${(amountCents / 100).toFixed(2)}`;
  },

  // Get status color for invoice status
  getStatusColor(status: string): string {
    switch (status) {
      case 'paid':
        return 'text-green-600 bg-green-100';
      case 'open':
        return 'text-yellow-600 bg-yellow-100';
      case 'draft':
        return 'text-gray-600 bg-gray-100';
      case 'void':
        return 'text-red-600 bg-red-100';
      case 'uncollectible':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  },

  // Get status text for display
  getStatusText(status: string): string {
    switch (status) {
      case 'paid':
        return 'Paid';
      case 'open':
        return 'Open';
      case 'draft':
        return 'Draft';
      case 'void':
        return 'Void';
      case 'uncollectible':
        return 'Uncollectible';
      default:
        return status;
    }
  },

  // Format date for display
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}; 